import identity from 'lodash/identity'
import pickBy from 'lodash/pickBy'
import { defineMessages } from 'react-intl'

import { isAbortError } from '@app/errors/AbortError'
import { AppError } from '@app/errors/AppError'

import { createCookieName } from '@app/utils/cookieNames'
import { createCookie } from '@app/utils/cookies'
import { captureException } from '@app/utils/errorReport/errorReport'
import { jsonParse, jsonSerialize } from '@app/utils/jsonSerialize'
import moment from '@app/utils/moment'
import { omit } from '@app/utils/omit'
import { pick } from '@app/utils/pick'
import { SerializedAddress } from '@app/utils/SerializedAddress'

import { getAbortSignal, withAbortSignal } from '@app/packages/abortContext/actions'
import { analyticsEvent } from '@app/packages/analytics/declaration'
import { convertFilterToQuery } from '@app/packages/announcementsFilter/actions'
import type { AnnouncementsFilter } from '@app/packages/announcementsFilter/AnnouncementsFilter'
import { asNetworkError } from '@app/packages/api/errors'
import {
  getAnnouncementsById,
  getAnnouncementsByQuery,
  getAnnouncementsSearch,
  getAnnouncementsSearchCount,
  getAnnouncementsSearchMap,
  getAnnouncementsUpcoming,
  postAnnouncements,
  putAnnouncementsById,
  putAnnouncementsByIdCancel,
  putAnnouncementsByIdHide,
} from '@app/packages/api/v2/actions/announcements'
import type { AnnouncementCreateRequest, AnnouncementQueryRequest } from '@app/packages/api/v2/types/params'
import { asError } from '@app/packages/asError/asError'
import { intoResultAsync, unwrapResult } from '@app/packages/Result/Result'

import {
  announcementsMetaSelector,
  announcementsRawIdsListSelector,
  failAnnouncementsCountSlice,
  failAnnouncementsLoadSlice,
  failAnnouncementUpdateSlice,
  resetAnnouncementsMetaSlice,
  setAnnouncementFilterSlice,
  setAnnouncementHiddenSlice,
  setAnnouncementsCountSlice,
  setAnnouncementsListSlice,
  setAnnouncementsMapSlice,
  setAnnouncementsSearchMetaSlice,
  setAnnouncementsUpcomingMetaSlice,
  startAnnouncementsCountSlice,
  startAnnouncementsLoadSlice,
  startAnnouncementUpdateSlice,
} from '@app/store/slices/announcements'
import { createAnnouncementBundleSelector } from '@app/store/slices/bundles'
import { intlSelector } from '@app/store/slices/intl'
import { profileUserSelector } from '@app/store/slices/profile'
import { createThunk } from '@app/store/thunk'

import { getAnalyticsRouter } from './analytics'
import { callV2Api } from './api'
import { getAbortControllerService } from './contexts/abortControllerService'
import { getCookies } from './contexts/cookies'

export function restoreAnnouncementsFilter() {
  return createThunk(async dispatch => {
    const cookies = dispatch(getCookies())
    dispatch(setAnnouncementFilterSlice(await filterCookie.get(cookies)))
  })
}

export function saveAnnouncementsFilter(filter: AnnouncementsFilter) {
  return createThunk(async dispatch => {
    const cookies = dispatch(getCookies())
    await filterCookie.set(cookies, filter)
  })
}

export function fetchAnnouncements(action?: 'next') {
  return createThunk(async (dispatch, getState) => {
    const abort = dispatch(getAbortControllerService())
    const meta = announcementsMetaSelector(getState())

    if (action === 'next' && meta.loading) return
    if (action === 'next' && meta.cursor === null) return

    const abortController = abort.createAbortController(ANNOUNCEMENTS_FETCH_ABORT_LABEL)

    const request = action === 'next' && meta.cursor ? { cursor: meta.cursor } : dispatch(convertFilterToQuery({ filter: meta.filter, page: 1, per_page: 10 }))

    dispatch(startAnnouncementsLoadSlice())

    const resp = await dispatch(withAbortSignal(abortController.signal, callV2Api(getAnnouncementsSearch(request)))).catch((e: unknown) => {
      const error = asError(e)
      dispatch(failAnnouncementsLoadSlice(error))
      if (!isAbortError(error)) {
        dispatch(getAnalyticsRouter()).send(
          analyticsEvent(() => ({
            // Loading the announcement list failed. Measures the request, not a press.
            type: 'sitter_fetch_announcements.error',
            data: { error: error.message, status: asNetworkError(error)?.status || 'unknown' },
          })).set('amplitude')
        )
      }
      throw e
    })

    dispatch(resetAnnouncementsMetaSlice(resp))
    dispatch(
      setAnnouncementsListSlice(
        resp.data.map(x => x.id),
        { append: 'cursor' in request }
      )
    )
    dispatch(setAnnouncementsSearchMetaSlice(resp.meta))

    const state = getState()
    const announcementsMeta = announcementsMetaSelector(state)
    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // The announcement list came back, with the filter it was asked for.
        type: 'sitter_fetch_announcements.fetch',
        data: {
          filter: pickBy({ ...announcementsMeta.filter }, identity),
          meta: { filter: announcementsMeta.filter, cursor: announcementsMeta.cursor },
          list: announcementsRawIdsListSelector(state),
        },
      })).set('amplitude')
    )

    return resp
  })
}

export const getMapAnnouncements = (filter: AnnouncementsFilter) => {
  return createThunk(async dispatch => {
    let cursor: string | null = null
    const per_page = 500
    let page = 1
    const request = dispatch(convertFilterToQuery({ filter, page: 1, per_page }))

    while (true) {
      if (dispatch(getAbortSignal())?.aborted) return

      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // Started loading the announcements behind the map.
          type: 'sitter_fetch_map_announcements.start',
          data: {
            filter: pickBy({ ...filter }, identity),
            page,
          },
        })).set('amplitude')
      )

      const respResult = await intoResultAsync(dispatch(callV2Api(getAnnouncementsSearchMap(cursor ? { cursor } : request))))

      if (respResult.error) {
        if (isAbortError(respResult.value)) return

        dispatch(getAnalyticsRouter()).send(
          analyticsEvent(() => ({
            // Loading the map's announcements failed.
            type: 'sitter_fetch_map_announcements.error',
            data: {
              filter: pickBy({ ...filter }, identity),
              page,
              error: respResult.value.message,
              status: asNetworkError(respResult.value)?.status || 'unknown',
            },
          })).set('amplitude')
        )
        throw respResult.value
      }

      const resp = respResult.value

      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // The map's announcements came back.
          type: 'sitter_fetch_map_announcements.success',
          data: {
            filter: pickBy({ ...filter }, identity),
            page,
          },
        })).set('amplitude')
      )

      dispatch(setAnnouncementsMapSlice(toAnnouncementMapPoints(resp), { initial: !cursor }))

      if (resp.data.length < per_page) return

      cursor = resp.meta.cursor
      ++page
    }
  })
}

export function fetchOwnAnnouncements() {
  return createThunk(async dispatch => {
    const abort = dispatch(getAbortControllerService())
    const abortController = abort.createAbortController(ANNOUNCEMENTS_FETCH_ABORT_LABEL)

    dispatch(startAnnouncementsLoadSlice())
    try {
      const resp = await dispatch(withAbortSignal(abortController.signal, callV2Api(getAnnouncementsUpcoming())))
      dispatch(resetAnnouncementsMetaSlice(resp))
      dispatch(
        setAnnouncementsListSlice(
          resp.data.map(x => x.id),
          { append: false }
        )
      )
      dispatch(setAnnouncementsUpcomingMetaSlice(resp.data.length))
      return resp
    } catch (e) {
      dispatch(failAnnouncementsLoadSlice(asError(e)))
      throw e
    }
  })
}

export const fetchAnnouncement = (id: string) =>
  createThunk(async dispatch => {
    const resp = await dispatch(callV2Api(getAnnouncementsById(id)))
    dispatch(resetAnnouncementsMetaSlice(resp))
    return resp
  })

export const fetchAnnouncementsByQuery = (request: AnnouncementQueryRequest) =>
  createThunk(async dispatch => {
    const resp = await dispatch(callV2Api(getAnnouncementsByQuery(request)))
    dispatch(resetAnnouncementsMetaSlice(resp))
    return resp
  })

export const createAnnouncement = (request: AnnouncementCreateRequest) => createThunk(async dispatch => await dispatch(callV2Api(postAnnouncements(request))))

export const updateAnnouncement = (id: string, request: AnnouncementCreateRequest) =>
  createThunk(async dispatch => await dispatch(callV2Api(putAnnouncementsById(id, request))))

export const cancelAnnouncement = (id: string) =>
  createThunk(async dispatch => {
    dispatch(startAnnouncementUpdateSlice(id))
    try {
      const resp = await dispatch(callV2Api(putAnnouncementsByIdCancel(id)))
      dispatch(resetAnnouncementsMetaSlice(resp))
      return resp
    } catch (e) {
      dispatch(failAnnouncementUpdateSlice(id, asError(e)))
      throw e
    }
  })

/** The counter polls this on every filter change, so it keeps its own request state. */
export const countAnnouncements = (params: Parameters<typeof getAnnouncementsSearchCount>[0]) =>
  createThunk(async dispatch => {
    dispatch(startAnnouncementsCountSlice())
    try {
      const resp = await dispatch(callV2Api(getAnnouncementsSearchCount(params)))
      dispatch(setAnnouncementsCountSlice(resp.meta.total))
      return resp
    } catch (e) {
      dispatch(failAnnouncementsCountSlice(asError(e)))
      throw e
    }
  })

/** The map reads two coordinates off each announcement's trimmed location. */
export const toAnnouncementMapPoints = (resp: {
  data: { id: string; relationships: { location: { data: { id: string } } } }[]
  included?: { id: string; attributes: { latitude: number; longitude: number } }[]
}) => {
  const locations = new Map((resp.included ?? []).map(location => [location.id, location]))

  return resp.data.map(announcement => {
    const location = locations.get(announcement.relationships.location.data.id)
    if (!location) throw new Error(`Location for announcement ${announcement.id} is missing from the response`)
    return { id: announcement.id, coords: { lat: location.attributes.latitude, lon: location.attributes.longitude } }
  })
}

export function hideAnnouncement(id: string) {
  return createThunk(async (dispatch, getState) => {
    const state = getState()
    const { formatMessage } = intlSelector(state)
    const announcement = unwrapResult(createAnnouncementBundleSelector(id)(state))
    const user = profileUserSelector(state)

    const parent = unwrapResult(announcement.parent)
    const location = unwrapResult(announcement.location)

    if (!user) {
      dispatch(setAnnouncementHiddenSlice(id, false))
      throw new AppError('User profile not found').withLocalizedMessage(formatMessage(messages.profile_not_found))
    }

    try {
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // A sitter hid an announcement. The payload carries the announcement as it was.
          type: 'hide_announcement',
          data: {
            announcement_id: id,
            sitter_id: user.id,
            parent_id: parent.user.id,
            ...omit(announcement.announcement.attributes, 'description'),
            ...(location ? pick(location.attributes, 'latitude', 'longitude') : {}),
            seconds_to_start: Math.round(
              moment(`${announcement.announcement.attributes.date} ${announcement.announcement.attributes.start}`, 'YYYYMMDD hh:mm').diff(moment()) / 1000
            ),
          },
        })).set('amplitude')
      )
    } catch (e) {
      captureException(e)
    }

    dispatch(setAnnouncementHiddenSlice(id, true))
    try {
      return await dispatch(callV2Api(putAnnouncementsByIdHide(id)))
    } catch (e) {
      dispatch(setAnnouncementHiddenSlice(id, false))
      throw e
    }
  })
}

const ANNOUNCEMENTS_FETCH_ABORT_LABEL = 'fetch-announcements'

/** Stores state of siiter's announcements filter */
const filterCookie = createCookie<AnnouncementsFilter>(
  createCookieName('kidsout__announcements_filter'),
  (i): AnnouncementsFilter => {
    try {
      return jsonParse(i, (val, def) => {
        return SerializedAddress.fromDataToAddress(val) ?? def()
      })
    } catch {
      return {}
    }
  },
  i => jsonSerialize(i, (_val, def) => def()),
  () => ({ expires: moment().add(1, 'year') })
)

const messages = defineMessages({
  announcement_not_found: { id: "store.actions.announcement.messages.announcement_not_found.171b", defaultMessage: 'Объявление №{announcement_id} не найдено' },
  profile_not_found: { id: "store.actions.announcement.messages.profile_not_found.0b7d", defaultMessage: 'Профиль пользователся не найден' },
})
